import { connect } from 'react-redux';
import React, { useEffect, FunctionComponent } from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { FormPackType } from '@upstart/harmony/lib/constants';
import { Dealer } from '@upstart/harmony/lib/legos/dealerTypes';
import SidebarContainer from '../SidebarContainer';
import formPacksActions from '../../redux/formPacks/actions';
import formsActions from '../../redux/forms/actions';
import ContentPage from '../ContentPage';
import ErrorMessage from '../../components/ErrorMessage';
import { FormPackEditor, NewFormPack } from '../../components/FormPackEditor';
import DealerNav from '../../components/DealerNav';
import { getOnlineFormPackMap } from './constants';

interface FormPacks {
  formPack: {
    id: string;
  };
  error: string | null;
  fetching: boolean;
}

interface Forms {
  forms: object[];
}

interface Dealers {
  dealer: Dealer;
}

interface Props {
  location: {
    state: {
      recordExists?: string;
    };
  };
  match: {
    params: {
      formPackId: string;
      dealerId: string;
    };
  };
  actions: Record<string, Function>;
  formPacks: FormPacks;
  forms: object[];
  dealer: Dealer;
}

const OnlineFormPack: FunctionComponent<Props> = ({
  location,
  match,
  actions,
  formPacks,
  forms,
  dealer,
}) => {
  useEffect(() => {
    const { formPackId, dealerId } = match.params;
    const { state } = location;
    const { recordExists } = state;
    // Fetch whether we should query the database from the history
    if (recordExists) {
      actions.formPackRequest(formPackId);
    } else {
      actions.resetFormPack(dealerId);
    }
    actions.formsRequest(dealerId);
  }, [match, location, actions]);

  const onUpdate = (formPack: object) => {
    actions.updateFormPackRequest(formPack);
  };

  const { dealerId, formPackId } = match.params;
  const { formPack, error, fetching } = formPacks;

  // victorytoyota-cash-purchase should be cash-purchase
  const formPackKey = formPackId.substring(
    dealerId.length + 1,
  ) as keyof ReturnType<typeof getOnlineFormPackMap>;

  // Get all form packs incl. Protection Plans
  const onlineFormPackMap = getOnlineFormPackMap(dealer);

  // Prefill new form pack
  const newFormPack = {
    id: formPackId,
    name: onlineFormPackMap[formPackKey].name,
    dealer_id: dealerId,
    type: FormPackType.ONLINE,
  };

  // We absolutely must update to preserve the fixed id we like
  return (
    <SidebarContainer title={dealer.name} logo={dealer.logo}>
      <div className="form-packs-table">
        <DealerNav />
        {error && <ErrorMessage error={error} />}
        {formPack.id && (
          <FormPackEditor
            formPack={formPack}
            forms={forms}
            fetching={fetching}
            onUpdate={onUpdate}
            type={FormPackType.ONLINE}
          />
        )}
        {!formPack.id && (
          <NewFormPack
            formPack={newFormPack}
            forms={forms}
            fetching={fetching}
            onCreate={onUpdate}
            type={FormPackType.ONLINE}
          />
        )}
      </div>
    </SidebarContainer>
  );
};

const mapStateToProps = ({
  formPacks,
  forms,
  dealers,
}: {
  formPacks: FormPacks;
  forms: Forms;
  dealers: Dealers;
}) => ({
  formPacks,
  forms: forms.forms,
  dealer: dealers.dealer,
});

const mapDispatchToProps = (dispatch: Dispatch<Function>) => ({
  actions: bindActionCreators(
    {
      ...formPacksActions,
      ...formsActions,
    },
    dispatch,
  ),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ContentPage(OnlineFormPack));
